/**
 * Deployment Service
 * Handles deployment API calls via the BFF (Backend for Frontend)
 *
 * POST /api/deployments  — create deployment request
 * GET  /api/deployments  — poll deployment status
 */

import axios from 'axios';
import { apiClient } from './apiClient';
import {
  BffDeploymentRequest,
  BffDeploymentResponse,
  BffDeploymentItem,
  BffEntityDetail,
  BffDeploymentTarget,
  BffDeploymentStatusListResponse,
  DeploymentResult,
  DeploymentStatus,
  DeploymentWarning,
} from './types';
import {
  DashboardPolicy,
  TenantPolicy,
} from '../components/policies/types';
import { TelemetryClient } from '../telemetry/TelemetryClient';
import { TelemetryConfig } from '../telemetry/TelemetryConfig';
import { FeatureName, TelemetryResult } from '../telemetry/TelemetryConstants';
import { createDeploymentTelemetryProperties } from '../telemetry/TelemetryDeploymentProperties';

// ========== Helpers ==========

/** Generate a deployment status-update key from entityId + tenantId */
function statusKey(entityId: string, tenantId: string): string {
  return `${entityId.toLowerCase()}::${tenantId.toLowerCase()}`;
}

// ========== Request Builders ==========

/**
 * Build BFF deployment request for Dashboard view (1 policy → multiple tenants)
 * Each tenant gets its own target with independently selected rules.
 */
export function buildDashboardDeployRequest(
  policy: DashboardPolicy,
  tenantRuleSelections: Map<string, Set<string>>,
): BffDeploymentRequest {
  const targets: BffDeploymentTarget[] = [];

  tenantRuleSelections.forEach((selectedRuleIds, tenantId) => {
    if (selectedRuleIds.size === 0) return;

    const selectedRules = policy.rules.filter(r => selectedRuleIds.has(r.ruleId));
    const ruleNames = selectedRules.map(r => r.ruleName);

    targets.push({
      tenantId,
      data: { rules: ruleNames },
    });
  });

  const entity: BffEntityDetail = {
    id: policy.policyName,
    type: policy.policyType,
    targets,
  };

  return {
    entityType: 'POLICY',
    entities: [entity],
  };
}

/**
 * Build BFF deployment request for Tenant view (multiple policies → 1 tenant)
 * Each policy becomes its own entity, all targeting the same tenant.
 */
export function buildTenantDeployRequest(
  policies: TenantPolicy[],
  policyRuleSelections: Record<string, Set<string>>,
  targetTenantId: string,
): BffDeploymentRequest {
  const entities: BffEntityDetail[] = [];

  policies.forEach(policy => {
    const selectedRuleIds = policyRuleSelections[policy.policyId];
    if (!selectedRuleIds || selectedRuleIds.size === 0) return;

    const selectedRules = policy.baseRules.filter(r => selectedRuleIds.has(r.ruleId));
    const ruleNames = selectedRules.map(r => r.ruleName);

    entities.push({
      id: policy.policyName,
      type: policy.policyType,
      targets: [
        {
          tenantId: targetTenantId,
          data: { rules: ruleNames },
        },
      ],
    });
  });

  return {
    entityType: 'POLICY',
    entities,
  };
}

// ========== API Calls ==========

/**
 * Get deployment status for a single deployment item.
 * GET /api/deployments?deploymentId={deploymentId}
 *
 * Unwraps the BFF list response and finds the specific entity item,
 * returning a flat object compatible with the polling hook.
 */
export async function getDeploymentStatus(
  deploymentId: string,
  _tenantId: string, // kept for signature compatibility; not sent to BFF
): Promise<{ deploymentId: string; deploymentStatus: string; error?: { errorCode: string; errorMessage: string }; warnings?: DeploymentWarning[] }> {
  const client = apiClient();

  const response = await client.get<BffDeploymentStatusListResponse>(
    '/deployments',
    { params: { deploymentId } },
  );

  const data = response.data;

  // Find the entity item matching our deploymentId
  for (const request of data.requests ?? []) {
    for (const item of request.entityItems ?? []) {
      if (item.deploymentId === deploymentId) {
        const warnings = (item.warnings ?? [])
          .filter(w => w.label || w.message)
          .map(w => ({ label: w.label ?? '', message: w.message ?? '' }));
        return {
          deploymentId,
          deploymentStatus: item.status || request.status || 'Pending',
          ...(request.failureReason ? {
            error: { errorCode: 'FAILURE', errorMessage: request.failureReason },
          } : {}),
          ...(warnings.length > 0 ? { warnings } : {}),
        };
      }
    }

    // If no entityItems match but requests[0] exists, use request-level status
    if (data.requests.length === 1) {
      return {
        deploymentId,
        deploymentStatus: request.status || 'Pending',
        ...(request.failureReason ? {
          error: { errorCode: 'FAILURE', errorMessage: request.failureReason },
        } : {}),
      };
    }
  }

  // Fallback — no matching data found
  return { deploymentId, deploymentStatus: 'Pending' };
}

/**
 * Execute a deployment request via BFF
 * POST /api/deployments
 */
export async function batchDeploy(request: BffDeploymentRequest): Promise<DeploymentResult> {
  // Count total targets for error reporting
  const targetTenantIds = request.entities.flatMap((entity) =>
    entity.targets.map((target) => target.tenantId),
  );
  const totalTargets = request.entities.reduce(
    (sum, e) => sum + (e.targets?.length ?? 0), 0,
  );
  const telemetry = TelemetryClient.getInstance(TelemetryConfig);
  const startedAt = Date.now();

  try {
    const client = apiClient();
    const response = await client.post<BffDeploymentResponse>(
      '/deployments',
      request,
    );

    const { batchId, accepted, failed, conflicts } = response.data;
    const acceptedCount = accepted?.length ?? 0;
    const failedCount = failed?.length ?? 0;
    const conflictCount = conflicts?.length ?? 0;

    // Build status updates map
    const statusUpdates = new Map<string, DeploymentStatus>();
    const acceptedItems: Array<{ deploymentId: string; tenantId: string; entityId: string }> = [];

    accepted?.forEach((item: BffDeploymentItem) => {
      if (item.entityId && item.targetTenantId) {
        const key = statusKey(item.entityId, item.targetTenantId);
        statusUpdates.set(key, 'InProgress');
        if (item.deploymentId) {
          acceptedItems.push({
            deploymentId: item.deploymentId,
            tenantId: item.targetTenantId,
            entityId: item.entityId,
          });
        }
      }
    });

    failed?.forEach((item: BffDeploymentItem) => {
      if (item.entityId && item.targetTenantId) {
        const key = statusKey(item.entityId, item.targetTenantId);
        statusUpdates.set(key, 'DeploymentFailed');
      }
    });

    conflicts?.forEach((item: BffDeploymentItem) => {
      if (item.entityId && item.targetTenantId) {
        const key = statusKey(item.entityId, item.targetTenantId);
        statusUpdates.set(key, 'InProgress'); // conflicts typically mean already in progress
      }
    });

    // Build user-friendly message
    let message: string;
    let success: boolean;

    if (failedCount === 0 && conflictCount === 0) {
      message = `Deployment initiated successfully for ${acceptedCount} item${acceptedCount !== 1 ? 's' : ''}.`;
      success = true;
    } else if (acceptedCount > 0) {
      const parts: string[] = [`${acceptedCount} accepted`];
      if (failedCount > 0) parts.push(`${failedCount} failed`);
      if (conflictCount > 0) parts.push(`${conflictCount} conflict${conflictCount !== 1 ? 's' : ''}`);
      message = `Partial deployment: ${parts.join(', ')}.`;
      success = true;
    } else {
      const errorDetails = failed
        ?.map((f: BffDeploymentItem) => f.reason)
        .filter(Boolean)
        .join('; ');
      message = `Deployment failed for all items.${errorDetails ? ` ${errorDetails}` : ''}`;
      success = false;
    }

    // Counts and identifiers only — never policy names, rule names, or tenant-supplied text.
    telemetry?.trackFeatureUsage({
      featureName: FeatureName.Purview,
      action: 'BatchDeploy',
      componentName: 'DeploymentService',
      result: success ? TelemetryResult.Success : TelemetryResult.Failure,
      durationMs: Date.now() - startedAt,
      ...createDeploymentTelemetryProperties({
        entityType: request.entityType,
        entityCount: request.entities.length,
        tenantIds: targetTenantIds,
        batchId,
      }),
      targetCount: totalTargets,
      acceptedCount,
      failedCount,
      conflictCount,
    });

    return { success, batchId: batchId ?? undefined, acceptedCount, failedCount, conflictCount, message, statusUpdates, acceptedItems };
  } catch (error: unknown) {
    telemetry?.trackFeatureUsage({
      featureName: FeatureName.Purview,
      action: 'BatchDeploy',
      componentName: 'DeploymentService',
      result: TelemetryResult.Failure,
      durationMs: Date.now() - startedAt,
      ...createDeploymentTelemetryProperties({
        entityType: request.entityType,
        entityCount: request.entities.length,
        tenantIds: targetTenantIds,
      }),
      targetCount: totalTargets,
    });

    // Handle HTTP error responses
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      const data = error.response.data;
      const apiMessage =
        data?.message || data?.error || data?.detail || data?.title || 'Unknown error';

      return {
        success: false,
        acceptedCount: 0,
        failedCount: totalTargets,
        conflictCount: 0,
        message: `Deployment request failed (${status}): ${apiMessage}`,
        statusUpdates: new Map(),
        acceptedItems: [],
      };
    }

    // Network / unknown errors
    const msg = error instanceof Error ? error.message : 'Unknown error';
    return {
      success: false,
      acceptedCount: 0,
      failedCount: totalTargets,
      conflictCount: 0,
      message: `Deployment request failed: ${msg}`,
      statusUpdates: new Map(),
      acceptedItems: [],
    };
  }
}
